import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthProvider';
import {
  MessageSquare,
  Calendar,
  Settings,
  Shield,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Globe,
  Tag,
  Sparkles,
  History
} from 'lucide-react';
import toast from 'react-hot-toast';
import { CommentExchangeHistory } from './CommentExchangeHistory';

interface CommentExchangeSettings {
  id: string;
  account_id: string;
  platform: string;
  enabled: boolean;
  daily_quota: number;
  languages: string[];
  categories: string[];
  min_words: number;
  comment_time_start?: string;
  comment_time_end?: string;
  min_minutes_between_comments?: number;
}

interface Account {
  id: string;
  platform: string;
  page_name: string;
  status: string;
}

interface QueueItem {
  id: string;
  status: string;
  scheduled_at: string;
  generated_comment: string;
  last_error: string;
  attempts: number;
  comment_exchange_posts_index: {
    caption_text: string;
    permalink: string;
  };
}

interface DailyStats {
  comments_sent: number;
  comments_received: number;
}

export function CommentExchange() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userPlan, setUserPlan] = useState<any>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [settings, setSettings] = useState<CommentExchangeSettings[]>([]);
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [dailyStats, setDailyStats] = useState<Record<string, DailyStats>>({});
  const [activeTab, setActiveTab] = useState<'overview' | 'accounts' | 'schedule' | 'history' | 'logs'>('overview');

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load user plan
      const { data: userData } = await supabase
        .from('users')
        .select('subscription_tier, subscription_end_date')
        .eq('id', user.id)
        .single();

      setUserPlan(userData);

      // Check if user has active plan
      const hasActivePlan = userData &&
        ['standard', 'premium', 'vip'].includes(userData.subscription_tier) &&
        (!userData.subscription_end_date || new Date(userData.subscription_end_date) > new Date());

      if (!hasActivePlan) {
        setLoading(false);
        return;
      }

      // Load accounts (only Instagram Direct and Facebook for Comment Exchange)
      const { data: accountsData } = await supabase
        .from('social_accounts')
        .select('*')
        .eq('owner_user_id', user.id)
        .in('platform', ['facebook', 'instagram_direct']);

      setAccounts(accountsData || []);

      // Load settings
      const { data: settingsData } = await supabase
        .from('comment_exchange_settings')
        .select('*')
        .eq('owner_user_id', user.id);

      setSettings(settingsData || []);

      // Load queue (only pending and recent items)
      const { data: queueData } = await supabase
        .from('comment_exchange_queue')
        .select('*, comment_exchange_posts_index(*)')
        .in('from_account_id', (accountsData || []).map(a => a.id))
        .in('status', ['queued', 'in_progress', 'failed'])
        .order('scheduled_at', { ascending: true })
        .limit(100);

      setQueueItems(queueData || []);

      // Load daily stats
      const today = new Date().toISOString().split('T')[0];
      const { data: statsData } = await supabase
        .from('comment_exchange_stats_daily')
        .select('*')
        .in('account_id', (accountsData || []).map(a => a.id))
        .eq('date', today);

      const statsMap: Record<string, DailyStats> = {};
      (statsData || []).forEach(stat => {
        statsMap[stat.account_id] = {
          comments_sent: stat.comments_sent || 0,
          comments_received: stat.comments_received || 0,
        };
      });

      console.log('[CommentExchange] Stats loaded:', {
        recordsCount: statsData?.length || 0,
        totalReceived: Object.values(statsMap).reduce((sum, s) => sum + s.comments_received, 0),
        totalSent: Object.values(statsMap).reduce((sum, s) => sum + s.comments_sent, 0),
        statsMap
      });

      setDailyStats(statsMap);

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleAccount = async (accountId: string, platform: string, enabled: boolean) => {
    try {
      const existing = settings.find(s => s.account_id === accountId && s.platform === platform);

      if (existing) {
        const { error } = await supabase
          .from('comment_exchange_settings')
          .update({ enabled: !enabled })
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('comment_exchange_settings')
          .insert({
            owner_user_id: user.id,
            account_id: accountId,
            platform: platform,
            enabled: true,
            daily_quota: 5,
            min_words: 8,
            comment_time_start: '09:00',
            comment_time_end: '21:00',
            min_minutes_between_comments: 15,
          });

        if (error) throw error;
      }

      toast.success('Settings updated');
      loadData();
    } catch (error) {
      console.error('Error toggling account:', error);
      toast.error('Failed to update settings');
    }
  };

  const updateSettings = async (accountId: string, platform: string, updates: Partial<CommentExchangeSettings>) => {
    try {
      const existing = settings.find(s => s.account_id === accountId && s.platform === platform);

      if (!existing) {
        toast.error('Please enable this account first');
        return;
      }

      const { error } = await supabase
        .from('comment_exchange_settings')
        .update(updates)
        .eq('id', existing.id);

      if (error) throw error;

      toast.success('Settings saved');
      loadData();
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to save settings');
    }
  };

  // Check if user has active plan
  const hasActivePlan = userPlan &&
    ['standard', 'premium', 'vip'].includes(userPlan.subscription_tier) &&
    (!userPlan.subscription_end_date || new Date(userPlan.subscription_end_date) > new Date());

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!hasActivePlan) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Upgrade Required</h2>
          <p className="text-gray-600 mb-6">
            Comment Exchange is available for Standard, Premium, and VIP plans.
          </p>
          <a
            href="https://aiagenthub.pl"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Upgrade Now
          </a>
        </div>
      </div>
    );
  }

  const totalEnabledAccounts = settings.filter(s => s.enabled).length;
  const todaysSent = Object.values(dailyStats).reduce((sum, s) => sum + s.comments_sent, 0);
  const todaysReceived = Object.values(dailyStats).reduce((sum, s) => sum + s.comments_received, 0);

  console.log('[CommentExchange] RENDER:', {
    dailyStatsKeys: Object.keys(dailyStats).length,
    todaysSent,
    todaysReceived,
    dailyStatsSnapshot: dailyStats
  });

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-6">
      {/* Header with glassmorphism */}
      <div className="relative overflow-hidden rounded-3xl p-8 backdrop-blur-xl bg-gradient-to-br from-white/80 to-blue-50/80 border border-white/50 shadow-2xl">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl -z-10" />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <MessageSquare className="w-8 h-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">Comment Exchange</h1>
            </div>
            <p className="text-gray-600 max-w-2xl mb-3">
              Boost engagement with automatic, high-quality comments on other users' posts.
            </p>
            <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 max-w-2xl">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <strong>Supported Platforms:</strong> Facebook and Instagram Direct only.
                Instagram Business accounts cannot be used due to API limitations.
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl px-4 py-3 border border-gray-200/50">
              <div className="text-sm text-gray-600">Plan</div>
              <div className="text-lg font-bold text-gray-900 capitalize">{userPlan.subscription_tier}</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl px-4 py-3 border border-gray-200/50">
              <div className="text-sm text-gray-600">Active Accounts</div>
              <div className="text-lg font-bold text-blue-600">{totalEnabledAccounts}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-white to-green-50 rounded-2xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Comments Sent Today</div>
              <div className="text-2xl font-bold text-gray-900">{todaysSent}</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Comments Received</div>
              <div className="text-2xl font-bold text-gray-900">{todaysReceived}</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-purple-50 rounded-2xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Scheduled</div>
              <div className="text-2xl font-bold text-gray-900">
                {queueItems.filter(q => ['queued', 'in_progress'].includes(q.status)).length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: TrendingUp },
            { id: 'accounts', label: 'Accounts', icon: Settings },
            { id: 'schedule', label: 'Schedule', icon: Calendar },
            { id: 'history', label: 'History', icon: History },
            { id: 'logs', label: 'Activity', icon: Clock },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  How It Works
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>System automatically finds relevant posts from other participating users</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>AI generates thoughtful, platform-compliant questions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Comments are distributed naturally throughout the day (30-45 min spacing)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Maximum 5 comments per account per day to stay within platform limits</span>
                  </li>
                </ul>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  Safety & Compliance
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li>✓ 100% unique comments generated for each post</li>
                  <li>✓ No spam patterns or repetitive content</li>
                  <li>✓ Compliant with Meta's automation policies</li>
                  <li>✓ Natural timing with random variations</li>
                  <li>✓ Only posts from last 24 hours are eligible</li>
                </ul>
              </div>
            </div>
          )}

          {/* Accounts Tab */}
          {activeTab === 'accounts' && (
            <div className="space-y-4">
              {accounts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No Facebook or Instagram accounts connected</p>
                </div>
              ) : (
                accounts.map(account => {
                  const accountSettings = settings.find(
                    s => s.account_id === account.id && s.platform === account.platform
                  );
                  const stats = dailyStats[account.id] || { comments_sent: 0, comments_received: 0 };

                  return (
                    <div
                      key={account.id}
                      className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            account.platform === 'facebook' ? 'bg-blue-100' : 'bg-pink-100'
                          }`}>
                            <MessageSquare className={`w-6 h-6 ${
                              account.platform === 'facebook' ? 'text-blue-600' :
                              account.platform === 'instagram_direct' ? 'text-purple-600' : 'text-pink-600'
                            }`} />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900">{account.page_name}</h3>
                            <p className="text-sm text-gray-500 capitalize">
                              {account.platform === 'instagram_direct' ? 'Instagram Direct' : account.platform}
                            </p>
                          </div>
                        </div>

                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={accountSettings?.enabled || false}
                            onChange={() => toggleAccount(account.id, account.platform, accountSettings?.enabled || false)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>

                      {accountSettings?.enabled && (
                        <div className="space-y-4 pt-4 border-t border-gray-200">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Daily Quota
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="20"
                                value={accountSettings.daily_quota}
                                onChange={(e) => updateSettings(account.id, account.platform, {
                                  daily_quota: parseInt(e.target.value)
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Min Words
                              </label>
                              <input
                                type="number"
                                min="5"
                                max="50"
                                value={accountSettings.min_words}
                                onChange={(e) => updateSettings(account.id, account.platform, {
                                  min_words: parseInt(e.target.value)
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-4 mt-4">
                            <div>
                              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                                <Clock className="w-4 h-4" />
                                Start Time
                              </label>
                              <input
                                type="time"
                                value={accountSettings.comment_time_start || '09:00'}
                                onChange={(e) => updateSettings(account.id, account.platform, {
                                  comment_time_start: e.target.value
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div>
                              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                                <Clock className="w-4 h-4" />
                                End Time
                              </label>
                              <input
                                type="time"
                                value={accountSettings.comment_time_end || '21:00'}
                                onChange={(e) => updateSettings(account.id, account.platform, {
                                  comment_time_end: e.target.value
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div>
                              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                                <Clock className="w-4 h-4" />
                                Min Minutes Between
                              </label>
                              <input
                                type="number"
                                min="5"
                                max="240"
                                value={accountSettings.min_minutes_between_comments || 15}
                                onChange={(e) => updateSettings(account.id, account.platform, {
                                  min_minutes_between_comments: parseInt(e.target.value)
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <p className="text-xs text-gray-500 mt-1">Delay between comments</p>
                            </div>
                          </div>

                          <div className="flex gap-4 text-sm mt-4">
                            <div className="flex items-center gap-2">
                              <TrendingUp className="w-4 h-4 text-green-600" />
                              <span className="text-gray-600">Sent: </span>
                              <span className="font-semibold">{stats.comments_sent}/{accountSettings.daily_quota}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MessageSquare className="w-4 h-4 text-blue-600" />
                              <span className="text-gray-600">Received: </span>
                              <span className="font-semibold">{stats.comments_received}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Schedule Tab */}
          {activeTab === 'schedule' && (
            <div className="space-y-4">
              {queueItems.length > 0 && (
                <div className="flex gap-3 text-sm">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg">
                    <Clock className="w-4 h-4" />
                    <span>Queued: {queueItems.filter(q => q.status === 'queued').length}</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg">
                    <Loader2 className="w-4 h-4" />
                    <span>Processing: {queueItems.filter(q => q.status === 'in_progress').length}</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg">
                    <XCircle className="w-4 h-4" />
                    <span>Failed: {queueItems.filter(q => q.status === 'failed').length}</span>
                  </div>
                </div>
              )}

              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {queueItems.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>No comments scheduled</p>
                  </div>
                ) : (
                  queueItems.map(item => (
                  <div
                    key={item.id}
                    className="bg-white rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {item.status === 'done' && <CheckCircle className="w-5 h-5 text-green-600" />}
                        {item.status === 'failed' && <XCircle className="w-5 h-5 text-red-600" />}
                        {item.status === 'queued' && <Clock className="w-5 h-5 text-blue-600" />}
                        {item.status === 'in_progress' && <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />}
                        <span className="text-sm font-medium capitalize">{item.status}</span>
                      </div>
                      <span className="text-sm text-gray-500">
                        {new Date(item.scheduled_at).toLocaleString()}
                      </span>
                    </div>

                    {item.generated_comment && (
                      <p className="text-sm text-gray-700 mb-2 italic">"{item.generated_comment}"</p>
                    )}

                    {item.last_error && (
                      <p className="text-sm text-red-600 mt-2">Error: {item.last_error}</p>
                    )}
                  </div>
                ))
              )}
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <CommentExchangeHistory />
          )}

          {/* Logs Tab */}
          {activeTab === 'logs' && (
            <div className="space-y-3">
              {queueItems.filter(q => ['done', 'failed'].includes(q.status)).length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No activity yet</p>
                </div>
              ) : (
                queueItems
                  .filter(q => ['done', 'failed'].includes(q.status))
                  .map(item => (
                    <div
                      key={item.id}
                      className="bg-white rounded-lg p-4 border border-gray-200"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {item.status === 'done' ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                          <span className="text-sm font-medium capitalize">{item.status}</span>
                        </div>
                        <span className="text-sm text-gray-500">
                          {new Date(item.scheduled_at).toLocaleString()}
                        </span>
                      </div>

                      {item.generated_comment && (
                        <p className="text-sm text-gray-700 italic">"{item.generated_comment}"</p>
                      )}

                      {item.last_error && (
                        <p className="text-sm text-red-600 mt-2">Error: {item.last_error}</p>
                      )}

                      {item.attempts > 1 && (
                        <p className="text-sm text-gray-500 mt-1">Attempts: {item.attempts}</p>
                      )}
                    </div>
                  ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
